import { useState, useEffect } from "react";
import { Link } from "wouter";
import { trpc } from "../lib/trpc";
import { Button, Card, Input, Select, Spinner, Stat, Badge } from "../components/ui";
import { useProgressStream } from "../hooks/useSSE";
import { JobProgress } from "../components/JobProgress";

type Target = "all" | "lightspeed" | "exact";

interface SafetyDiff {
  reason?: string | null;
  sku?: string | null;
  articleCode?: string | null;
  itemCode?: string | null;
  desc?: string | null;
}

function parseDiff(v: unknown): SafetyDiff {
  if (v == null) return {};
  if (typeof v === "string") { try { return JSON.parse(v) as SafetyDiff; } catch { return {}; } }
  return v as SafetyDiff;
}

const targetLabel = (t: string) => (t === "lightspeed" ? "Lightspeed" : "ExactOnline");
const actionLabel = (t: string) => (t === "lightspeed" ? "Hide" : "Discontinue");

/**
 * Change candidates a mass-change safety cap held back — Lightspeed hides (engine.ts's runReconcileLs)
 * and ExactOnline orphan-discontinues (runReconcileExact) — too many to apply blanket-live in one
 * reconcile, but each individually a real candidate. Review here one at a time or in a chosen batch, or
 * exclude a SKU from that system altogether.
 */
export default function SafetyReview() {
  const utils = trpc.useUtils();
  const [target, setTarget] = useState<Target>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  useEffect(() => setPage(1), [search, target, pageSize]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  // Live per-op progress (mirrors ChangeSet.tsx's "Post 1 LIVE"/"Post N LIVE" modals) — without this,
  // clicking an action gives no visible sign anything is happening until the list happens to refetch.
  const [livePost, setLivePost] = useState<{ opId: number; label: string; target: string; startedAt: number } | null>(null);
  const [batchStartedAt, setBatchStartedAt] = useState<number | null>(null);
  const [batchHidden, setBatchHidden] = useState(false);
  const { log } = useProgressStream();

  const q = trpc.reconcile.safetyReview.list.useQuery({ target, search: search || undefined, page, pageSize }, { refetchInterval: 15000 });
  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const pageCount = q.data?.pageCount ?? 1;
  const counts = trpc.reconcile.safetyReview.count.useQuery(undefined, { refetchInterval: 15000 });

  const invalidate = () => { utils.reconcile.safetyReview.list.invalidate(); utils.reconcile.safetyReview.count.invalidate(); };
  const applyOp = trpc.reconcile.applyOp.useMutation({ onSuccess: invalidate, onError: invalidate }); // error shown in the live modal
  const applyOpsLive = trpc.reconcile.applyOpsLive.useMutation({
    onSuccess: invalidate,
    onError: (e) => { alert(`Failed to start:\n${e.message}`); setBatchStartedAt(null); },
  });
  const excludeSku = trpc.config.excludedSku.upsert.useMutation({ onSuccess: invalidate, onError: (e) => alert(e.message) });
  const groupIds = trpc.reconcile.safetyReview.groupIds.useMutation();

  const liveSteps = livePost ? log.filter((e) => e.job === "applyOp" && e.at >= livePost.startedAt - 500) : [];
  const batchSteps = batchStartedAt !== null ? log.filter((e) => e.job === "applyBatch" && e.at >= batchStartedAt) : [];
  const batchDone = batchSteps.some((e) => e.step === "done");
  const batchRunning = batchStartedAt !== null && !batchDone;

  const toggleOne = (id: number) => setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAllOnPage = () => setSelected((prev) => {
    if (allOnPageSelected) { const next = new Set(prev); for (const r of rows) next.delete(r.id); return next; }
    const next = new Set(prev); for (const r of rows) next.add(r.id); return next;
  });
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const ids = await utils.reconcile.safetyReview.ids.fetch({ target, search: search || undefined });
      setSelected(new Set(ids));
    } finally { setSelectingAll(false); }
  };

  // Selected ops may span more than one change set (or both destinations) — group so each gets its own
  // applyOpsLive call. Looked up directly by id (not via the paginated `list`, which caps at 500/page and
  // can't return thousands of rows in one shot for a "select all matching" selection).
  const applySelected = async () => {
    if (!confirm(`Apply ${selected.size.toLocaleString()} selected change(s) live now?`)) return;
    try {
      const groups = await groupIds.mutateAsync({ ids: [...selected] });
      const byChangeSet = new Map<number, number[]>();
      for (const g of groups) { const list = byChangeSet.get(g.changeSetId) ?? []; list.push(g.id); byChangeSet.set(g.changeSetId, list); }
      setBatchStartedAt(Date.now());
      setBatchHidden(false);
      for (const [changeSetId, opIds] of byChangeSet) await applyOpsLive.mutateAsync({ id: changeSetId, opIds });
      setSelected(new Set());
    } catch (e) {
      // A plain onClick handler's rejection is otherwise invisible — no alert, no modal, nothing (this is
      // exactly the "selected 2,422, clicked apply, nothing happened" failure mode). Always surface it.
      alert(`Failed to start: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Safety review</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            When a reconcile finds an unusually large batch of hides (Lightspeed) or discontinues (ExactOnline) at once, it's usually a sign of an incomplete PBS sync, not a real business change — so instead of applying them all blindly, or silently throwing them away, they land here. Nothing on this page has been sent anywhere yet: go through them one at a time or in a chosen batch, and either apply the change for real or exclude that SKU so it's never proposed again.
          </p>
        </div>
        <Input placeholder="Search SKU / item code / name…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </div>

      {/* Durable "still applying" signal — recomputed fresh from the DB lock on every poll, so it's
          correct even on a fresh page load or after navigating away and back, unlike the progress
          modal/pill below (local component state that's gone the moment this page unmounts). Shows
          each running set's own processed/total straight from the same query, instead of just "something,
          somewhere is running" — no need to go dig it out of Sync history for basic progress. */}
      {counts.data?.running && batchStartedAt === null && (
        <div className="flex flex-col gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm text-indigo-800">
          {(counts.data.runningSets ?? []).map((r) => (
            <div key={r.id} className="flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
              Applying <Link href={`/reconcile/${r.id}`} className="font-medium underline">change set #{r.id}</Link> — {r.processed}/{r.total} processed ({r.percent}%){r.failed > 0 ? `, ${r.failed} failed` : ""}. Started earlier, possibly from another visit to this page — it's safe, the counts above update as it progresses.
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <button onClick={() => setTarget("all")} className={`text-left ${target === "all" ? "" : "opacity-60 hover:opacity-100"}`}>
          <Stat label="All destinations" value={(counts.data?.total ?? 0).toLocaleString()} accent={target === "all" ? "indigo" : "slate"} />
        </button>
        <button onClick={() => setTarget("lightspeed")} className={`text-left ${target === "lightspeed" ? "" : "opacity-60 hover:opacity-100"}`}>
          <Stat label="🛒 Lightspeed hides" value={(counts.data?.lightspeed ?? 0).toLocaleString()} accent={target === "lightspeed" ? "indigo" : "slate"} />
        </button>
        <button onClick={() => setTarget("exact")} className={`text-left ${target === "exact" ? "" : "opacity-60 hover:opacity-100"}`}>
          <Stat label="📒 ExactOnline discontinues" value={(counts.data?.exact ?? 0).toLocaleString()} accent={target === "exact" ? "indigo" : "slate"} />
        </button>
      </div>

      <Card title={`${total.toLocaleString()} item${total === 1 ? "" : "s"} awaiting a decision`}>
        {q.isLoading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing pending for this filter — either no safety cap has been hit recently, or everything here has already been resolved.</p>
        ) : (
          <>
            {selected.size > 0 && (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2.5">
                <div className="text-sm text-red-800">
                  {selected.size.toLocaleString()} selected
                  {selected.size < total && (
                    <button onClick={selectAllMatching} disabled={selectingAll} className="ml-2 font-semibold text-red-700 underline hover:text-red-900">
                      {selectingAll ? "selecting…" : `Select all ${total.toLocaleString()}`}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
                  <Button variant="danger" size="sm" disabled={applyOpsLive.isPending} onClick={applySelected}>
                    {applyOpsLive.isPending ? "Applying…" : `Apply ${selected.size.toLocaleString()} now`}
                  </Button>
                </div>
              </div>
            )}
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-8 py-2"><input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage} className="cursor-pointer" /></th>
                  <th>Item</th>
                  <th>Reason</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const d = parseDiff(r.diff);
                  // entityKey is always the PBS/match code (LS ops just also carry it separately as
                  // `sku`; Exact orphan ops never did — this is the one place that data actually lives).
                  const sku = d.sku ?? r.entityKey;
                  const code = d.articleCode ?? d.itemCode ?? "—";
                  const codeLabel = r.target === "lightspeed" ? "shop code" : "ExactOnline code";
                  return (
                    <tr key={r.id} className={`align-top ${selected.has(r.id) ? "bg-red-50/40" : "hover:bg-slate-50"}`}>
                      <td className="py-3"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} className="cursor-pointer" /></td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Badge color={r.target === "lightspeed" ? "indigo" : "blue"}>{targetLabel(r.target)}</Badge>
                          <div className="font-medium text-slate-800">{d.desc || r.entityKey}</div>
                        </div>
                        <div className="mt-0.5 text-xs text-slate-400">SKU {sku} · {codeLabel} {code}</div>
                      </td>
                      <td className="py-3 text-slate-600">{d.reason ?? "—"}</td>
                      <td className="py-3">
                        <div className="flex flex-col items-end gap-1.5">
                          <div className="flex gap-2">
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={applyOp.isPending}
                              onClick={() => {
                                if (!confirm(`${actionLabel(r.target)} "${d.desc ?? r.entityKey}" on ${targetLabel(r.target)} now?`)) return;
                                setLivePost({ opId: r.id, label: d.desc ?? r.entityKey, target: r.target, startedAt: Date.now() });
                                applyOp.reset();
                                applyOp.mutate({ opId: r.id });
                              }}
                            >
                              {actionLabel(r.target)} now
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={excludeSku.isPending}
                              onClick={() => { if (confirm(`Exclude SKU ${sku} from ${targetLabel(r.target)} going forward? It will be left unmanaged there.`)) excludeSku.mutate({ system: r.target as "lightspeed" | "exact", sku, label: d.desc ?? undefined }); }}
                            >
                            Exclude SKU
                            </Button>
                          </div>
                          <div className="flex gap-3 text-xs">
                            {r.viewUrl && <a href={r.viewUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline">View in {targetLabel(r.target)} ↗</a>}
                            <Link href={`/products/${sku}`} className="font-medium text-indigo-600 hover:underline">View product</Link>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <span>Page {page} of {pageCount}</span>
                <Select value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value))} className="text-xs">
                  <option value={100}>100 / page</option>
                  <option value={250}>250 / page</option>
                  <option value={500}>500 / page</option>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Live single-item progress — step-by-step over SSE (job "applyOp"), same pattern as ChangeSet's "Post 1 LIVE" */}
      {livePost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">
                {actionLabel(livePost.target)} on {targetLabel(livePost.target)} <span className="ml-1 text-xs font-normal text-slate-400">{livePost.label}</span>
              </h3>
              {!applyOp.isPending && <button onClick={() => setLivePost(null)} className="text-slate-400 hover:text-slate-700">✕</button>}
            </div>
            <div className="space-y-1.5 p-5">
              {liveSteps.length === 0 && applyOp.isPending && <div className="text-sm text-slate-500">Starting…</div>}
              {liveSteps.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5">{e.percent < 0 ? "❌" : e.detail?.includes("✓") || e.step === "complete" ? "✅" : "⏳"}</span>
                  <span className={e.percent < 0 ? "text-red-700" : "text-slate-700"}>{e.detail || e.step}</span>
                </div>
              ))}
              {applyOp.isPending && (
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full bg-indigo-600 transition-all" style={{ width: `${Math.max(5, liveSteps.at(-1)?.percent ?? 5)}%` }} />
                </div>
              )}
              {applyOp.isSuccess && <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">Applied — verified ✓</div>}
              {applyOp.isError && <div className="mt-2 break-words rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{applyOp.error.message}</div>}
            </div>
            {!applyOp.isPending && (
              <div className="flex justify-end border-t border-slate-100 px-5 py-3">
                <Button variant="secondary" onClick={() => setLivePost(null)}>Close</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch "Apply selected" progress — overall progress over SSE (job "applyBatch") */}
      {batchStartedAt !== null && !batchHidden && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Applying selected changes</h3>
              {!batchRunning && <button onClick={() => { setBatchStartedAt(null); setSelected(new Set()); }} className="text-slate-400 hover:text-slate-700">✕</button>}
            </div>
            <div className="space-y-2 p-5">
              {batchSteps.at(-1) ? <JobProgress ev={batchSteps.at(-1)!} /> : <div className="text-sm text-slate-500">Starting…</div>}
              <p className="text-xs text-slate-400">Runs entirely on the server — closing your browser or minimizing this window does not stop it. Minimize any time; a small progress pill stays on screen so you can reopen this.</p>
              {batchDone && <div className="mt-1 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">{batchSteps.at(-1)?.detail || "Done"} ✓</div>}
            </div>
            <div className="flex justify-end border-t border-slate-100 px-5 py-3">
              <Button variant="secondary" onClick={() => { if (batchRunning) { setBatchHidden(true); } else { setBatchStartedAt(null); setBatchHidden(false); } }}>{batchRunning ? "Minimize" : "Close"}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Persistent reopen affordance — without this, minimizing the modal above leaves NO progress
          indicator anywhere on this page (unlike ChangeSet.tsx, which always has its own change-set-scoped
          progress card as a fallback). This page spans many change sets, so there's no equivalent; this
          pill is the only way back in once minimized. */}
      {batchStartedAt !== null && batchHidden && (
        <button
          onClick={() => setBatchHidden(false)}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg transition hover:bg-indigo-500"
        >
          <span className={`h-2 w-2 rounded-full ${batchRunning ? "animate-pulse bg-white" : "bg-emerald-300"}`} />
          {batchRunning ? (batchSteps.at(-1)?.detail ?? "Applying…") : "Done — click to view"}
        </button>
      )}
    </div>
  );
}
